'use client';

import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc/client';

interface PartnerInquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  labels: {
    title: string;
    subtitle: string;
    name: string;
    namePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    website: string;
    websitePlaceholder: string;
    country: string;
    countryPlaceholder: string;
    submit: string;
    submitting: string;
    successTitle: string;
    successMessage: string;
    errorTitle: string;
    errorMessage: string;
    close: string;
  };
}

export function PartnerInquiryModal({ isOpen, onClose, labels }: PartnerInquiryModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    country: '',
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const submitMutation = trpc.partner.submitInquiry.useMutation({
    onSuccess: () => {
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        website: '',
        country: '',
      });
    },
    onError: () => {
      setSubmitStatus('error');
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('idle');
    submitMutation.mutate(formData);
  };

  const handleClose = () => {
    setSubmitStatus('idle');
    setFormData({
      name: '',
      email: '',
      website: '',
      country: '',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay with gradient background */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-primary-800/70 to-neutral-800/80 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-primary-600 transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="p-8">
          {submitStatus === 'success' ? (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-serif text-primary-900 mb-2">
                {labels.successTitle}
              </h3>
              <p className="text-gray-600 text-sm mb-5">
                {labels.successMessage}
              </p>
              <button
                onClick={handleClose}
                className="bg-primary-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-primary-550 transition-colors text-sm"
              >
                {labels.close}
              </button>
            </div>
          ) : (
            <>
              <div className="mb-5 text-center">
                <h2 className="text-2xl font-serif text-primary-900 mb-1.5">
                  {labels.title}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {labels.subtitle}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
                    {labels.name} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-sm"
                    placeholder={labels.namePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
                    {labels.email} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-sm"
                    placeholder={labels.emailPlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
                    {labels.website}
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-sm"
                    placeholder={labels.websitePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
                    {labels.country} *
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-sm"
                    placeholder={labels.countryPlaceholder}
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-red-800">{labels.errorTitle}</p>
                        <p className="text-xs text-red-700">{labels.errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitMutation.isPending}
                  className="w-full bg-primary-600 text-white py-2.5 px-6 rounded-full font-semibold hover:bg-primary-550 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg text-sm mt-4"
                >
                  {submitMutation.isPending ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      {labels.submitting}
                    </>
                  ) : (
                    labels.submit
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

